import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  createMatterLitigationDateV1,
  deleteMatterLitigationDateV1,
  updateMatterLitigationDateV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static";
import {
  type APIError,
  type APIResponse,
  type Matter,
  type MatterLitigationDate,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const useCreateMatterLitigationDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterLitigationDateV1>[0]) =>
      createMatterLitigationDateV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating matter litigation date");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Matter litigation date created successfully");
    },
  });
};

export const useUpdateMatterLitigationDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterLitigationDateV1>[0]) =>
      updateMatterLitigationDateV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating matter litigation date");
    },
    onSettled: (_data, _error, variables) => {
      const invalidate = () => {
        void queryClient.invalidateQueries({
          queryKey: [queryKeys.matters, variables.matterId],
        });
        void queryClient.invalidateQueries({
          queryKey: [queryKeys.matters],
        });
      };
      setTimeout(invalidate, 500);
      setTimeout(invalidate, 1000);
      setTimeout(invalidate, 2500);
    },
    onSuccess: (response, variables) => {
      queryClient.setQueryData<APIResponse<Matter>>(
        matterDetailQueryKey(variables.matterId),
        (matterResponse) => {
          if (!matterResponse) return matterResponse;
          return {
            ...matterResponse,
            data: {
              ...matterResponse.data,
              litigationDates: matterResponse.data.litigationDates.map(
                (litigationDate) =>
                  litigationDate.id === response.data.id
                    ? response.data
                    : litigationDate,
              ),
            },
          };
        },
      );
      toast.success("Matter litigation date updated successfully");
    },
  });
};

export const useRemoveMatterLitigationDatePlanConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      matterId: string;
      litigationDates: Array<Pick<MatterLitigationDate, "dateModified" | "id">>;
    }) => {
      await Promise.all(
        data.litigationDates.map((litigationDate) =>
          updateMatterLitigationDateV1({
            ...litigationDate,
            matterId: data.matterId,
            planId: null,
          }),
        ),
      );
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error removing litigation date plan");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Litigation date plan connection removed successfully");
    },
  });
};

export const useDeleteMatterLitigationDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterLitigationDateV1>[0]) =>
      deleteMatterLitigationDateV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting matter litigation date");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Matter litigation date deleted successfully");
    },
  });
};
